import { useIsDarkMode } from '@flowus/common';
import { cx } from '@flowus/common/cx';
import type { FC } from 'react';
import { memo, useCallback, useEffect, useState } from 'react';
import { useIsExistModalId } from 'src/common/components/next-modal';
import { Tooltip } from 'src/common/components/tooltip';
import { useEnableAI } from 'src/hooks/block/use-enable-AI';
import { useGetUnreadCount } from 'src/hooks/public/use-get-unread-count';
import { useCurrentUser, useCurrentUserId } from 'src/hooks/user';
import { Images } from 'src/image';
import { Modals } from 'src/modals';
import { setAppUiState, useIsEditingPage, useServiceCenterRedBadge } from 'src/services/app';
import { useDrawerViewWidth } from 'src/services/app-support/drawer-width';
import { useGetSyncService } from 'src/services/sync';
import { $searchParams } from 'src/utils';
import { bizTracker } from 'src/utils/biz-tracker';
import { judgeSharePage, judgeSubscribePage, useGetPageId } from 'src/utils/getPageId';
import { useGetRightPageId } from 'src/utils/right-utils';
import { useDrawerOpenState } from 'src/views/main/drawer/shared';
import { useGlobalModalQueueEffect } from '../../services/global-modal-queue';
import { TransferButton } from '../transfer-list/transfer-button';
import { CouponCountDown } from '@flowus/common/components/coupon-count-down';
import { useLocalState } from 'src/services/app-support/local-state';
import dayjs from 'dayjs';
import { useOpenUpgradeSpace } from 'src/views/main/setting-modal/use-open-setting-modal';
import { OpenSettingFrom } from 'src/views/main/setting-modal/type';
import { useCurrentSpace } from 'src/hooks/space';
import { PRODUCT_AI_TITLE } from 'src/const/title';
import { Icon } from 'src/common/components/icon';
import { request } from 'src/common/request';
import { useOpenUpgradeSpacePlan } from '../upgrade-space-plan';
import { UpgradeSpacePlanType } from '../upgrade-space-plan/types';
import { useCheckLogin } from 'src/hooks/share/use-check-login';

export const ServiceCenter: FC = memo(() => {
  const { drawerWidth } = useDrawerViewWidth();
  const serviceCenterRedBadge = useServiceCenterRedBadge();
  const currentUser = useCurrentUser();
  const isLogin = Boolean(currentUser.uuid);
  const isEditingPage = useIsEditingPage();
  const rightPageId = useGetRightPageId();
  const [isDrawerOpen, setDrawState] = useDrawerOpenState();
  const getUnreadCount = useGetUnreadCount();
  const { enableAI } = useEnableAI();
  const openUpgradeSpace = useOpenUpgradeSpace();
  const spaceId = useCurrentSpace().uuid;
  useIsDarkMode();
  useGlobalModalQueueEffect();
  const isSharePage = judgeSharePage();
  // 不包含我的订阅页面
  const [isVip, setVip] = useState(false);
  const openUpgradeSpacePlan = useOpenUpgradeSpacePlan();

  const sync = useGetSyncService();
  const userId = useCurrentUserId();
  const isNotificationsPopupOpen = useIsExistModalId(Modals.MESSAGE_NOTIFICATION);

  const isSubScribePage = judgeSubscribePage();
  const isOnlySharePage = isSharePage && !isSubScribePage;

  const timestamp = useLocalState((state) => {
    if (!state.$showCountDown?.[spaceId]) return null;
    return dayjs(state.$showCountDown[spaceId]?.startTime).add(1, 'hour').valueOf();
  });

  const fetchUnreadCount = useCallback(() => {
    void getUnreadCount().then(({ strategyGuides, messageBoard }) => {
      if (strategyGuides > 0 || messageBoard > 0) {
        setAppUiState({ $serviceCenterRedBadge: true });
      }
    });
  }, [getUnreadCount]);
  useEffect(() => {
    if (!isLogin) return;
    if (isSubScribePage) {
      void request.infra.getUserIsVip().then((vip) => {
        setVip(vip);
      });
    }
  }, [isLogin, isSubScribePage]);

  useEffect(() => {
    if (isSharePage) return;
    return sync.subscribeNotifications(() => {
      void fetchUnreadCount();
    });
  }, [sync, isNotificationsPopupOpen, userId, fetchUnreadCount, isSharePage]);

  useEffect(() => {
    if (isSharePage) return;
    fetchUnreadCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSharePage]);

  if (!isLogin || isOnlySharePage || $searchParams.print || $searchParams.embed || __PRIVATE__) {
    return null;
  }

  const openHelper = (type: 'ai' | 'service') => {
    bizTracker.event('service_ball_clk');

    if (type === 'ai') {
      if (isDrawerOpen && ['ai-chat-right', 'ai-chat'].includes(isDrawerOpen)) {
        void setDrawState(false);
      } else {
        bizTracker.event('flowus_ai_entrance', { user_id: currentUser.uuid });
        void setDrawState(rightPageId ? 'ai-chat-right' : 'ai-chat');
      }
      return;
    }

    if (type === 'service') {
      if (isDrawerOpen && ['service-center-right', 'service-center'].includes(isDrawerOpen)) {
        void setDrawState(false);
      } else {
        void setDrawState(rightPageId ? 'service-center-right' : 'service-center');
      }
    }
  };

  return (
    <div
      className={cx(
        'service-center fixed right-10 bottom-10 transition-all z-50 select-none space-y-2.5 flex flex-col items-center justify-center',
        isEditingPage ? 'duration-500 opacity-0' : 'duration-100 opacity-100',
        { 'bottom-[150px]': isSubScribePage }
      )}
      style={{
        transform: `translateX(${isDrawerOpen ? `-${drawerWidth}px` : '20px'})`,
      }}
    >
      {timestamp && !isSubScribePage && (
        <Tooltip popup="专属限时优惠券" placement="left">
          <CouponCountDown
            timestamp={timestamp}
            img={Images.couponCountDown}
            onClick={() => {
              openUpgradeSpace(OpenSettingFrom.activity);
            }}
          />
        </Tooltip>
      )}
      <TransferButton />
      {(enableAI || isSubScribePage) && (
        <Tooltip
          popup={PRODUCT_AI_TITLE}
          placement="right"
          className="w-9 h-9 flex items-center justify-center next-modal animate-click overflow-hidden rounded-full shadow-light-sm"
          onClick={() => {
            if (!isSubScribePage || isVip) {
              openHelper('ai');
            } else {
              openUpgradeSpacePlan({ type: UpgradeSpacePlanType.subscribeAiLimit });
            }
          }}
        >
          <Icon name="IcAiMiddleFill" size="small" />
        </Tooltip>
      )}
      {!isSubScribePage && (
        <Tooltip
          onClick={() => openHelper('service')}
          placement="right"
          popup="点我查看使用帮助信息"
          className="animate-click btn-helper relative w-9 h-9 flex items-center justify-center next-modal rounded-full shadow-light-sm overflow-visible"
        >
          {!(__BUILD_IN__ || __PRIVATE__) && serviceCenterRedBadge && (
            <div className="absolute right-0 top-0 w-2.5 h-2.5 z-10 bg-red rounded-full" />
          )}
          <Icon size="small" name="IcQuestionMiddleBold" />
        </Tooltip>
      )}
    </div>
  );
});

// 知识库的button
export const HelpAIButton: FC = () => {
  const currentUser = useCurrentUser();
  const isLogin = Boolean(currentUser.uuid);
  const { drawerWidth } = useDrawerViewWidth();
  const [isDrawerOpen, setDrawState] = useDrawerOpenState();
  const pageId = useGetPageId();
  const [isKnowledge, setIsKnowledge] = useState(false);
  const checkLogin = useCheckLogin();

  const isShare = judgeSharePage();
  const openHelper = (type: 'ai' | 'service') => {
    bizTracker.event('service_ball_clk');
    if (type === 'ai') {
      if (isDrawerOpen && ['ai-chat-right', 'ai-chat'].includes(isDrawerOpen)) {
        void setDrawState(false);
      } else {
        bizTracker.event('flowus_ai_entrance', { user_id: currentUser.uuid });
        void setDrawState('ai-chat');
      }
    }
  };
  useEffect(() => {
    if (!isLogin) return;
    // 检查是不是知识库的页面
    void request.ai.infraCheckKnowledge(pageId).then((res) => {
      setIsKnowledge(res);
    });
  }, [isLogin, pageId]);

  if (!isShare) return null;
  if (!isKnowledge) return null;
  return (
    <div
      className={cx(
        'service-center fixed right-10 bottom-[150px] transition-all z-50 select-none space-y-2.5 flex flex-col items-center justify-center duration-100 opacity-100 '
      )}
      style={{
        transform: `translateX(${isDrawerOpen ? `-${drawerWidth}px` : '20px'})`,
      }}
    >
      <Tooltip
        popup={PRODUCT_AI_TITLE}
        placement="right"
        className="w-9 h-9 flex items-center justify-center next-modal animate-click overflow-hidden rounded-full shadow-light-sm"
        onClick={() => {
          if (checkLogin()) {
            openHelper('ai');
          }
        }}
      >
        <Icon name="IcAiMiddleFill" size="small" />
      </Tooltip>
    </div>
  );
};
